/**
 * useFunnelViewport Hook - MA-ARCH-164
 * 
 * Manages viewport state including hover, exit transitions, and node heights.
 * Extracted from FunnelVisualization.jsx for cleaner state separation.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Hook to manage funnel viewport interaction state.
 * Handles hover state, exit transitions, and measured node heights.
 * 
 * @returns {Object} - Viewport state and handlers
 */
export function useFunnelViewport() {
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  
  // Transient exit state for synchronized hover-exit transitions
  const [exitingNodeId, setExitingNodeId] = useState(null);
  const exitTimeoutRef = useRef(null);
  
  // Track actual measured heights for all nodes
  const [nodeHeights, setNodeHeights] = useState(new Map());
  const nodeHeightsRef = useRef(new Map());
  
  // Store the expanded height when entering hover, preserve during exit
  const expandedHeightRef = useRef(null);
  
  // The "geometry node" is the node whose expanded geometry should be used
  // for all layout calculations. During hover, it's hoveredNodeId. During exit,
  // it's exitingNodeId.
  const geometryNodeId = hoveredNodeId || exitingNodeId;
  
  // Cleanup exit timeout on unmount
  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);
  
  /**
   * Handle node hover with synchronized exit transitions.
   * @param {string|null} nodeId - The hovered node ID or null
   */
  const handleNodeHover = useCallback((nodeId) => {
    // Clear any pending exit timeout
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
    
    if (nodeId !== null) {
      // HOVER ENTER - clear any ongoing exit state and set new hover
      setExitingNodeId(null);
      setHoveredNodeId(nodeId);
    } else {
      // HOVER EXIT - preserve geometry during transition
      const currentHoveredId = hoveredNodeId;
      if (currentHoveredId) {
        // Capture expanded height before clearing hover
        expandedHeightRef.current = nodeHeightsRef.current.get(currentHoveredId) || nodeHeights.get(currentHoveredId);
        // Set exiting state to preserve geometry
        setExitingNodeId(currentHoveredId);
      }
      // Clear hover immediately - triggers CSS collapse animation
      setHoveredNodeId(null);
      
      // After CSS transition completes, clear exit state
      exitTimeoutRef.current = setTimeout(() => {
        setExitingNodeId(null);
        expandedHeightRef.current = null;
        exitTimeoutRef.current = null;
      }, 200); // Matches CSS transition-duration-200
    }
  }, [hoveredNodeId, nodeHeights]);
  
  /**
   * Callback to receive actual measured height from nodes.
   * @param {string} nodeId - The node ID
   * @param {number} height - The measured height
   * @param {boolean} isHovered - Whether the node is hovered
   */
  const handleCardHeightChange = useCallback((nodeId, height, isHovered) => {
    // Update ref immediately for sync access
    nodeHeightsRef.current.set(nodeId, height);
    // Update state to trigger re-render
    setNodeHeights(prev => {
      const newMap = new Map(prev);
      newMap.set(nodeId, height);
      return newMap;
    });
  }, []);
  
  /**
   * Get the measured height for a node.
   * @param {string} nodeId - The node ID
   * @returns {number|null} - The height or null
   */
  const getNodeHeight = useCallback((nodeId) => {
    return nodeHeightsRef.current.get(nodeId) || nodeHeights.get(nodeId) || null;
  }, [nodeHeights]);
  
  /**
   * Get the expanded height ref value (for exit transitions).
   * @returns {number|null}
   */
  const getExpandedHeight = useCallback(() => {
    return expandedHeightRef.current;
  }, []);
  
  return {
    // State
    hoveredNodeId,
    exitingNodeId,
    geometryNodeId,
    nodeHeights,
    
    // Handlers
    handleNodeHover,
    handleCardHeightChange,
    getNodeHeight,
    getExpandedHeight,
    
    // Refs (for advanced usage)
    nodeHeightsRef,
    expandedHeightRef,
  };
}

export default useFunnelViewport;
